import React, { useMemo } from "react";
import { Flex, Text, Box, Progress } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { VideoNewIcon } from "@root/assets/icons/VideoNewIcon";
import { CompletedIcon } from "@root/assets/icons/CompletedIcon";
import { formatDuration } from "@root/utils/date-helper";

interface ChapterVideoItemProps {
  video: any;
  videoIdx: number;
  enrolled: boolean;
  courseProgressData: any;
  onVideoSelect: (video: any, chapterIdx: number) => void;
  chapterIdx: number;
}

const ChapterVideoItem: React.FC<ChapterVideoItemProps> = ({
  video,
  videoIdx,
  enrolled,
  courseProgressData,
  onVideoSelect,
  chapterIdx,
}) => {
  const { t } = useTranslation("common");

  const progressItem = courseProgressData?.[0]?.find(
    (item: any) => item?.video?.id === video?.id
  );
  const isCompleted = progressItem?.completed === true;

  // Calculate video progress percentage (rounded)
  const videoProgress = useMemo(() => {
    if (isCompleted) {
      return 100;
    }
    if (progressItem?.progress !== undefined && progressItem?.progress !== null) {
      const progress = typeof progressItem.progress === "number"
        ? progressItem.progress
        : parseFloat(progressItem.progress || "0");
      if (!Number.isNaN(progress)) {
        return Math.round(Math.max(0, Math.min(100, progress)));
      }
    }
    return 0;
  }, [progressItem, isCompleted]);

  return (
    <Flex
      key={video?.id || videoIdx}
      alignItems="center"
      py={3}
      px={6}
      gap={"8px"}
      cursor="pointer"
      _hover={{ bg: "gray.50" }}
      onClick={() => onVideoSelect(video, chapterIdx)}
    >
      <Box>
        <VideoNewIcon color={isCompleted ? "#00A177" : "#40464B"} />
      </Box>

      <Flex align="center" gap="8px" flex={1}>
        <Text
          fontSize="14px"
          fontWeight={500}
          color={isCompleted ? "#00A177" : "#2F2F34"}
        >
          {video?.title}
        </Text>
        {isCompleted && <CompletedIcon />}
      </Flex>

      {enrolled && (
        <Flex align="center" gap={2} minW="120px">
          <Text fontSize="12px" color="#5C6670">
            {videoProgress}%
          </Text>
          <Progress
            value={videoProgress}
            height="8px"
            width="80px"
            colorScheme="blue"
            borderRadius="40px"
          />
        </Flex>
      )}

      <Text fontSize="14px" color="#5C6670">
        {formatDuration(video?.duration || 0)}
      </Text>
      {!enrolled && (
        <Text fontSize="12px" color="#FF6B35" ml={2}>
          {t("courseDetail.preview")}
        </Text>
      )}
    </Flex>
  );
};

export default ChapterVideoItem;
